import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useBusinessUnit } from "../../context/BusinessUnitContext";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Center,
  Button,
  Icon,
  Text,
  Stack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  IconButton,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  VStack,
  useToast,
  HStack,
  Avatar,
  Badge,
  Divider,
  Tooltip,
  Alert,
  AlertIcon,
  CloseButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Skeleton,
  Progress,
} from "@chakra-ui/react";
import { FaCloudUploadAlt, FaFileAlt, FaPaperPlane, FaTrashAlt, FaClipboard, FaPlus, FaInfoCircle, FaCode, FaClipboardList } from "react-icons/fa";
import {
  PageHeader,
  Panel,
  StatusBadge,
  MiniIconButton,
  FilterSearch,
  EmptyState,
} from "../../components/Portal/ui";

// Compact primary button props matching the dense template scale (mirrors AsapCV).
const primaryBtnProps = {
  variant: "primary",
  size: "sm",
  h: "32px",
  px: "14px",
  fontSize: "12px",
};

// Tiny uppercase form label, template `.label` / FormLabel scale.
const formLabelProps = {
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "portal.muted",
  sx: { marginBottom: "6px !important" },
};

// 31px-tall select sized to the template controls.
const selectProps = {
  size: "sm",
  height: "31px",
  lineHeight: "31px",
  sx: {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    lineHeight: "31px !important",
    height: "31px !important",
  },
};

// 28px-tall compact select used inside the dense matrix rows.
const matrixSelectProps = {
  size: "xs",
  height: "28px",
  lineHeight: "28px",
  sx: {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    lineHeight: "28px !important",
    height: "28px !important",
  },
};

// Per-section accent palette for the skill matrix groups. Mirrors the skill
// badge colours: technical=purple, functional/role=orange, business=green.
const MATRIX_SECTIONS = {
  role: { bg: "orange.50", border: "orange.200", heading: "orange.700" },
  technical: { bg: "purple.50", border: "purple.200", heading: "purple.700" },
  functional: { bg: "orange.50", border: "orange.200", heading: "orange.700" },
  business: { bg: "green.50", border: "green.200", heading: "green.700" },
};

// Combined page: left = assessment documents list, right = assessment submission form
const AsapSkills = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const [searchParams, setSearchParams] = useSearchParams();

  // Linked consultant (passed from consultant detail modal)
  const [linkedConsultantId, setLinkedConsultantId] = useState(() => {
    const id = searchParams.get("consultantId");
    return id ? parseInt(id, 10) : null;
  });
  const [linkedConsultantName, setLinkedConsultantName] = useState(
    () => searchParams.get("consultantName") || null
  );

  const unlinkConsultant = () => {
    setLinkedConsultantId(null);
    setLinkedConsultantName(null);
    searchParams.delete("consultantId");
    searchParams.delete("consultantName");
    searchParams.delete("consultantEmail");
    setSearchParams(searchParams, { replace: true });
  };

  const importanceHelpText = t("asapSkills.form.importance.help", {
    defaultValue:
      "Primary skills are the must-have requirements and are weighted more in the assessment. Secondary skills are nice-to-have and have less impact.",
  });
  const importanceHelpAria = t("asapSkills.form.importance.helpAria", {
    defaultValue: "What do Primary and Secondary mean?",
  });
  const ImportanceHelpIcon = () => (
    <Tooltip label={importanceHelpText} placement="top" hasArrow openDelay={200}>
      <IconButton
        aria-label={importanceHelpAria}
        icon={<Icon as={FaInfoCircle} boxSize={3.5} />}
        size="xs"
        variant="ghost"
        colorScheme="gray"
      />
    </Tooltip>
  );

  // Documents state
  const [groups, setGroups] = useState([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [downloadingKey, setDownloadingKey] = useState("");
  
  // Compute monthly assessment counts for chart (last 6 months)
  const [monthData, setMonthData] = useState([]);
  // Search & pagination state
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalItems, setTotalItems] = useState(null);

  useEffect(() => {
    const counts = {};
    (groups || []).forEach((g) => {
      const d = new Date(g.created_at);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    const arr = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      arr.push({
        label: date.toLocaleString('default', { month: 'short' }),
        value: counts[key] || 0,
      });
    }
    setMonthData(arr);
  }, [groups]);

  // Debounce search input and reset page
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // BU context must be initialized before any effects using it
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  // Fetch documents function - reusable for both initial load and periodic updates
  const fetchDocuments = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setIsLoadingDocs(true);
    }
    try {
      const config = {
        params: {
          groupType: "assessment",
          search: search || undefined,
          page,
          pageSize,
        },
        ...(selectedBusinessUnitId ? { headers: { "X-Business-Unit-Id": selectedBusinessUnitId } } : {}),
      };
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/documents`,
        config
      );
      const payload = response.data;
      let newGroups = [];
      let total = null;
      if (Array.isArray(payload)) {
        newGroups = payload;
      } else if (payload && Array.isArray(payload.items)) {
        newGroups = payload.items;
        total = typeof payload.total === "number" ? payload.total : (typeof payload.count === "number" ? payload.count : null);
      } else if (payload && Array.isArray(payload.data)) {
        newGroups = payload.data;
        total = typeof payload.total === "number" ? payload.total : (typeof payload.count === "number" ? payload.count : null);
      }
      setTotalItems(total);
      const computedHasNext = typeof total === "number" ? (page * pageSize) < total : (newGroups.length === pageSize);
      setHasNextPage(computedHasNext);
      
      // Smart comparison: only update if there are actual changes
      if (isInitialLoad) {
        setGroups(newGroups);
      } else {
        // For periodic updates, compare with existing groups
        setGroups(prevGroups => {
          // Check if there are new groups or if existing groups have been updated
          const hasNewGroups = newGroups.length !== prevGroups.length;
          const hasUpdatedGroups = newGroups.some(newGroup => {
            const existingGroup = prevGroups.find(prevGroup => prevGroup.id === newGroup.id);
            return !existingGroup || 
                   existingGroup.updated_at !== newGroup.updated_at ||
                   existingGroup.created_at !== newGroup.created_at;
          });
          
          // Only update if there are actual changes
          if (hasNewGroups || hasUpdatedGroups) {
            return newGroups;
          }
          return prevGroups; // No changes, keep existing state
        });
      }
    } catch (error) {
      console.error("Failed to fetch assessment documents", error);
      // Only show toast for initial load errors, not for periodic updates
      if (isInitialLoad) {
        const isExpired = error?.response?.status === 402;
        toast({
          title: isExpired
            ? t("asapSkills.toasts.trialExpiredTitle")
            : t("asapSkills.toasts.fetchErrorTitle"),
          description: isExpired
            ? t("asapSkills.toasts.trialExpiredDescription")
            : error.response?.data?.message ||
              t("asapSkills.toasts.fetchErrorDescription"),
          status: isExpired ? "warning" : "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      if (isInitialLoad) {
        setIsLoadingDocs(false);
      }
    }
  };

  // Initial fetch documents only after BU context is ready; include BU header when available
  useEffect(() => {
    if (!isBUContextLoaded) return; // wait for BU context
    // If user has BUs, wait until one is selected; if user has none, proceed without header
    if (Array.isArray(selectedBusinessUnitId) ? selectedBusinessUnitId.length === 0 : false) return;
    fetchDocuments(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, isBUContextLoaded, selectedBusinessUnitId, page, pageSize, search]);

  // Set up periodic fetching every 45 seconds
  useEffect(() => {
    if (!isBUContextLoaded) return; // wait for BU context
    // If user has BUs, wait until one is selected; if user has none, proceed without header
    if (Array.isArray(selectedBusinessUnitId) ? selectedBusinessUnitId.length === 0 : false) return;
    
    const interval = setInterval(() => {
      fetchDocuments(false); // false = not initial load
    }, 45000); // 45 seconds

    // Cleanup interval on unmount or dependency change
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBUContextLoaded, selectedBusinessUnitId, page, pageSize, search]);

  const handleKindDownload = async (reqId, kind, filenameBase) => {
    setDownloadingKey(`${reqId}:${kind}`);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/documents/group/${reqId}/download?kind=${encodeURIComponent(kind)}`,
        { responseType: "blob" }
      );

      const blob = response.data;
      const dispo = response.headers["content-disposition"] || "";
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(dispo);
      const serverFilename = match ? match[1].replace(/["']/g, "") : null;

      const typeToExt = (t) => {
        if (t === "application/pdf") return ".pdf";
        if (t === "application/zip") return ".zip";
        if (t === "text/plain") return ".txt";
        return "";
      };

      const fallbackName = `${filenameBase || "assessment"}_${kind}${typeToExt(blob.type)}`;
      const finalName = serverFilename || fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", finalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download document", error);
      let description = t("asapSkills.toasts.downloadErrors.generic");
      if (error.response) {
        if (error.response.status === 400)
          description = t("asapSkills.toasts.downloadErrors.notReady");
        else if (error.response.status === 403)
          description = t("asapSkills.toasts.downloadErrors.forbidden");
        else if (error.response.status === 404)
          description = t("asapSkills.toasts.downloadErrors.notFound");
      }
      toast({
        title: t("asapSkills.toasts.downloadFailedTitle"),
        description,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloadingKey("");
    }
  };

  // Assessment form state
  const { user } = useAuth();
  const { selectedBusinessUnit, businessUnits } = useBusinessUnit();
  const defaultCompanyCode = selectedBusinessUnit?.unit_code || user?.companyCode || "";
  const [companyCode] = useState(defaultCompanyCode);
  const [language, setLanguage] = useState("francais");
  const [jobDescFile, setJobDescFile] = useState(null);
  const [jobDescDragActive, setJobDescDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(null);
  const [userHasSetDuration, setUserHasSetDuration] = useState(false);

  // Custom skills added by the user (not part of the extracted job matrix)
  const [customTechSkills, setCustomTechSkills] = useState([]); // { name, technical_subcategory, requiresCoding }[]
  const [customFuncSkills, setCustomFuncSkills] = useState([]); // string[]
  const [customBusinessSkills, setCustomBusinessSkills] = useState([]); // string[]
  const [customRoleSkills, setCustomRoleSkills] = useState([]); // string[] (treated as functional for submission)

  // Add-skill modal state
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [newSkillName, setNewSkillName] = useState("");
  const [newSkillType, setNewSkillType] = useState("technique"); // technique | fonctionnel | metier | role
  const [newSkillTechnicalSubcategory, setNewSkillTechnicalSubcategory] = useState("dev"); // dev | non-dev
  const [newSkillDifficulty, setNewSkillDifficulty] = useState("");
  const [newSkillImportance, setNewSkillImportance] = useState("primary"); // primary | secondary

  const hasSelectedBU = Boolean(selectedBusinessUnitId);
  const noBUAvailable = isBUContextLoaded && (!businessUnits || businessUnits.length === 0);

  // Job matrix data and selected competencies
  const [jobMatrixData, setJobMatrixData] = useState(null);
  const [loadingJobMatrix, setLoadingJobMatrix] = useState(false);
  const [sector, setSector] = useState("");
  const [subSector, setSubSector] = useState("");
  const [techCompetences, setTechCompetences] = useState([]);
  const [funcCompetences, setFuncCompetences] = useState([]);
  const [metierCompetences, setMetierCompetences] = useState([]);

  // Skills count for slider math
  const selectedSkillsCount = useMemo(
    () => techCompetences.length + funcCompetences.length + metierCompetences.length,
    [techCompetences, funcCompetences, metierCompetences]
  );
  const extractedSkillsCount = useMemo(() => {
    if (!jobMatrixData) return 0;
    const tech = getMatrixSkillsByType(jobMatrixData, "technique").length;
    const func = getMatrixSkillsByType(jobMatrixData, "fonctionnel").length;
    const metier = getMatrixSkillsByType(jobMatrixData, "metier").length;
    return tech + func + metier;
  }, [jobMatrixData]);
  const skillsCount = autoMode ? extractedSkillsCount : selectedSkillsCount;

  // Split job matrix into technical/functional/business items and role-based items (for display only)
  const {
    technicalItems,
    functionalItems,
    businessItems,
    roleItems,
  } = useMemo(() => {
    if (!jobMatrixData) {
      return {
        technicalItems: [],
        functionalItems: [],
        businessItems: [],
        roleItems: [],
      };
    }

    const techSource = getMatrixSkillsByType(jobMatrixData, "technique");
    const funcSource = getMatrixSkillsByType(jobMatrixData, "fonctionnel");
    const metierSource = getMatrixSkillsByType(jobMatrixData, "metier");

    const technicalItems = techSource.filter((item) => !item.isRole);
    const functionalItems = funcSource.filter((item) => !item.isRole);
    const businessItems = metierSource.filter((item) => !item.isRole);

    const roleItems = [
      ...techSource
        .filter((item) => item.isRole)
        .map((item) => ({
          technology: item["compétence_technique"],
          sourceType: "technique",
          importance: item.importance,
          requiresCoding: item.requiresCoding,
          technical_subcategory: item.technical_subcategory,
          skill_context: item.skill_context,
        })),
      ...funcSource
        .filter((item) => item.isRole)
        .map((item) => ({
          technology: item["compétence_fonctionnelle"],
          sourceType: "fonctionnel",
          importance: item.importance,
          requiresCoding: item.requiresCoding,
          skill_context: item.skill_context,
        })),
      ...metierSource
        .filter((item) => item.isRole)
        .map((item) => ({
          technology: item["compétence_metier"],
          sourceType: "metier",
          importance: item.importance,
          requiresCoding: item.requiresCoding,
          skill_context: item.skill_context,
        })),
    ];

    return {
      technicalItems,
      functionalItems,
      businessItems,
      roleItems,
    };
  }, [jobMatrixData]);

  // Display lists = extracted matrix + user-added custom skills
  const technicalDisplayItems = useMemo(() => {
    const custom = (customTechSkills || [])
      .map((entry) => {
        const isStringEntry = typeof entry === "string";
        const name = String(
          isStringEntry
            ? entry
            : entry?.name || entry?.technology || entry?.["compétence_technique"] || ""
        )
          .trim();
        if (!name) return null;

        const rawSubcategory = String(
          isStringEntry ? "" : entry?.technical_subcategory || entry?.technicalSubcategory || ""
        )
          .trim()
          .toLowerCase();
        const technical_subcategory = rawSubcategory === "non-dev" ? "non-dev" : "dev";
        const requiresCoding = isStringEntry
          ? technical_subcategory === "dev"
          : entry?.requiresCoding === true || technical_subcategory === "dev";

        return {
          __custom: true,
          "compétence_technique": name,
          technical_subcategory,
          requiresCoding,
        };
      })
      .filter(Boolean);
    return [...technicalItems, ...custom];
  }, [technicalItems, customTechSkills]);
  const functionalDisplayItems = useMemo(() => {
    const custom = (customFuncSkills || []).map((name) => ({
      __custom: true,
      "compétence_fonctionnelle": name,
    }));
    return [...functionalItems, ...custom];
  }, [functionalItems, customFuncSkills]);
  const businessDisplayItems = useMemo(() => {
    const custom = (customBusinessSkills || []).map((name) => ({
      __custom: true,
      "compétence_metier": name,
    }));
    return [...businessItems, ...custom];
  }, [businessItems, customBusinessSkills]);
  const roleDisplayItems = useMemo(() => {
    const custom = (customRoleSkills || []).map((name) => ({
      __custom: true,
      technology: name,
      sourceType: "fonctionnel",
      isRole: true,
    }));
    return [...roleItems, ...custom];
  }, [roleItems, customRoleSkills]);

  const getSkillDurationRange = (type, skill = {}) => {
    const imp = skill?.importance === "secondary" ? "secondary" : "primary";

    // Role timing overrides all categories.
    if (skill?.isRole === true) {
      return imp === "secondary"
        ? { min: 5, max: 8.5 }
        : { min: 10, max: 17 };
    }

    if (type === "technique") {
      const normalizedSubcategory = String(
        skill?.technical_subcategory || skill?.technicalSubcategory || ""
      )
        .trim()
        .toLowerCase();
      const isDev =
        normalizedSubcategory === "dev" ||
        (normalizedSubcategory !== "non-dev" && skill?.requiresCoding === true);

      if (isDev) {
        return imp === "secondary"
          ? { min: 8, max: 14 }
          : { min: 14, max: 18 };
      }
      return imp === "secondary"
        ? { min: 6, max: 10 }
        : { min: 10, max: 16.5 };
    }

    // Methodology / functional skills
    if (type === "fonctionnel") {
      return imp === "secondary" ? { min: 5, max: 8 } : { min: 15, max: 21.25 };
    }

    // Business / metier skills
    return imp === "secondary" ? { min: 5, max: 8.5 } : { min: 15, max: 22.5 };
  };

  // Slider bounds
  const sliderMin = useMemo(() => {
    if (autoMode) return 30;
    const sum = (arr, type) =>
      (arr || []).reduce(
        (acc, s) => acc + getSkillDurationRange(type, s).min,
        0
      );
    return sum(techCompetences, "technique") +
      sum(funcCompetences, "fonctionnel") +
      sum(metierCompetences, "metier");
  }, [autoMode, techCompetences, funcCompetences, metierCompetences]);
  const sliderMax = useMemo(() => {
    if (autoMode) return 60;
    const sum = (arr, type) =>
      (arr || []).reduce(
        (acc, s) => acc + getSkillDurationRange(type, s).max,
        0
      );
    return sum(techCompetences, "technique") +
      sum(funcCompetences, "fonctionnel") +
      sum(metierCompetences, "metier");
  }, [autoMode, techCompetences, funcCompetences, metierCompetences]);

  // Keep duration in range; default to max (full coverage) when S changes
  useEffect(() => {
    if (!autoMode && skillsCount <= 0) {
      setDurationMinutes(null);
    } else {
      // Always default to max whenever skills count or bounds change
      setDurationMinutes(sliderMax);
    }
  }, [autoMode, skillsCount, sliderMax]);

  // Planned coverage display
  const plannedCoverage = useMemo(() => {
    if (!skillsCount || !durationMinutes) return null;
    const perSkillMinutes = durationMinutes / skillsCount;
    const themesPerSkill = Math.max(4, Math.min(6, Math.floor(perSkillMinutes / 3)));
    const questionsPerSkill = themesPerSkill * 2;
    const totalQuestions = skillsCount * questionsPerSkill;
    return { themesPerSkill, questionsPerSkill, totalQuestions };
  }, [skillsCount, durationMinutes]);

  // Candidate list (name + email rows)
  const [candidateRows, setCandidateRows] = useState(() => {
    const initEmail = searchParams.get("consultantEmail") || "";
    const initName = searchParams.get("consultantName") || "";
    if (initEmail) {
      return [{ name: initName, email: initEmail, valid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(initEmail) }];
    }
    return [{ name: initName || "", email: "", valid: false }];
  });

  // UI helpers
  const jobDescInputRef = useRef(null);
  const difficultyChoices = [
    t("asapSkills.form.difficulty.junior"),
    t("asapSkills.form.difficulty.confirme"),
    t("asapSkills.form.difficulty.senior"),
    t("asapSkills.form.difficulty.seniorConfirme"),
    t("asapSkills.form.difficulty.expert"),
  ];
  const importanceOptions = [
    {
      value: "primary",
      label: t("asapSkills.form.importance.primary", { defaultValue: "Primary" }),
    },
    {
      value: "secondary",
      label: t("asapSkills.form.importance.secondary", {
        defaultValue: "Secondary",
      }),
    },
  ];

  // Ensure modal difficulty defaults to the first option when translations are ready
  useEffect(() => {
    if (!newSkillDifficulty && difficultyChoices[0]) {
      setNewSkillDifficulty(difficultyChoices[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficultyChoices.join("|")]);

  const normalizeSkillName = (s) => String(s || "").trim().replace(/\s+/g, " ");
  function normalizeTechnicalSubcategory(value) {
    const raw = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");
    if (raw === "nondev" || raw === "non-dev" || raw === "noncoding" || raw === "non-coding") {
      return "non-dev";
    }
    return "dev";
  }
  function getCustomSkillName(entry) {
    if (typeof entry === "string") return normalizeSkillName(entry);
    if (entry && typeof entry === "object") {
      return normalizeSkillName(
        entry.name ||
          entry.technology ||
          entry["compétence_technique"] ||
          entry["compétence_fonctionnelle"] ||
          entry["compétence_metier"] ||
          ""
      );
    }
    return "";
  }
  function toBooleanFlag(value) {
    if (value === true || value === 1) return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return ["true", "1", "yes", "y", "oui"].includes(normalized);
    }
    return false;
  }
  function isCodingSkill(skill = {}) {
    const normalizedSubcategory = String(
      skill?.technical_subcategory || skill?.technicalSubcategory || ""
    )
      .trim()
      .toLowerCase();
    if (["dev", "development", "coding", "code"].includes(normalizedSubcategory)) {
      return true;
    }
    if (normalizedSubcategory === "non-dev" || normalizedSubcategory === "non dev") {
      return false;
    }
    return toBooleanFlag(skill?.requiresCoding);
  }
  function normalizeMatrixItem(item, type) {
    if (!item || typeof item !== "object") return null;

    const skillNameByType = {
      technique:
        item["compétence_technique"] ||
        item.competence_technique ||
        item.competenceTechnique ||
        item.technology ||
        item.skill ||
        item.competence ||
        item.name ||
        "",
      fonctionnel:
        item["compétence_fonctionnelle"] ||
        item.competence_fonctionnelle ||
        item.competenceFonctionnelle ||
        item.technology ||
        item.skill ||
        item.competence ||
        item.name ||
        "",
      metier:
        item["compétence_metier"] ||
        item.competence_metier ||
        item.competenceMetier ||
        item.technology ||
        item.skill ||
        item.competence ||
        item.name ||
        "",
    };

    const technology = String(skillNameByType[type] || "").trim();
    if (!technology) return null;

    const normalized = {
      ...item,
      niveau: item.niveau || item.difficulty_level || item.level || "",
      importance: item.importance,
      requiresCoding:
        toBooleanFlag(item.requiresCoding) ||
        toBooleanFlag(item.requires_coding) ||
        toBooleanFlag(item.coding_required) ||
        toBooleanFlag(item.is_coding),
      technical_subcategory:
        item.technical_subcategory || item.technicalSubcategory || "",
      skill_context: item.skill_context,
      isRole: item.isRole === true || item.is_role === true,
    };

    if (type === "technique") normalized["compétence_technique"] = technology;
    if (type === "fonctionnel") normalized["compétence_fonctionnelle"] = technology;
    if (type === "metier") normalized["compétence_metier"] = technology;

    return normalized;
  }

  function toSkillArray(value) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      return Object.values(value).filter((v) => v && typeof v === "object");
    }
    return [];
  }

  function getMatrixSkillsByType(data, type) {
    if (!data) return [];
    const matrix = data.MATRICE || {};

    const candidatesByType = {
      technique: [
        data.MATRICE_TECHNIQUES,
        matrix.MATRICE_TECHNIQUES,
        matrix.techniques,
        matrix.technique,
        matrix.technical,
      ],
      fonctionnel: [
        data.MATRICE_FONCTIONNELLES,
        matrix.MATRICE_FONCTIONNELLES,
        matrix.fonctionnelles,
        matrix.fonctionnel,
        matrix.functional,
      ],
      metier: [
        data.MATRICE_METIER,
        matrix.MATRICE_METIER,
        matrix.metier,
        matrix.business,
      ],
    };

    const candidates = candidatesByType[type] || [];
    for (const candidate of candidates) {
      const list = toSkillArray(candidate);
      if (list.length > 0) {
        return list.map((item) => normalizeMatrixItem(item, type)).filter(Boolean);
      }
    }
    return [];
  }

  const renderSkillLabelWithContext = (technology, skillContext, requiresCoding = false) => {
    const contextText =
      typeof skillContext === "string" ? skillContext.trim() : "";
    const skillLabel = contextText ? (
      <Tooltip
        label={contextText}
        placement="top-start"
        hasArrow
        openDelay={200}
      >
        <Text
          as="span"
          cursor="help"
          textDecoration="underline dotted"
          textUnderlineOffset="2px"
        >
          {technology}
        </Text>
      </Tooltip>
    ) : (
      <Text as="span">{technology}</Text>
    );

    return (
      <HStack as="span" spacing={1.5}>
        {skillLabel}
        {requiresCoding && (
          <Tooltip
            label={t("asapSkills.form.codingSkillTooltip", {
              defaultValue: "This skill includes coding questions in the code editor.",
            })}
            placement="top"
            hasArrow
            openDelay={200}
          >
            <Box as="span" color="gray.500" display="inline-flex" alignItems="center">
              <Icon as={FaCode} boxSize={3} />
            </Box>
          </Tooltip>
        )}
      </HStack>
    );
  };

  const openAddSkillModal = () => {
    // Adding a skill implies manual mode; auto-mode would ignore job_matrix on submit.
    if (autoMode) {
      setAutoMode(false);
      toast({
        title: t("asapSkills.form.addSkill.autoModeDisabledTitle", {
          defaultValue: "Automatic mode disabled",
        }),
        description: t("asapSkills.form.addSkill.autoModeDisabledDescription", {
          defaultValue: "To add a skill, we switched to manual mode so your changes are sent.",
        }),
        status: "info",
        duration: 4000,
        isClosable: true,
      });
    }
    setNewSkillName("");
    setNewSkillType("technique");
    setNewSkillTechnicalSubcategory("dev");
    setNewSkillDifficulty(difficultyChoices[0] || "");
    setNewSkillImportance("primary");
    setIsAddSkillOpen(true);
  };

  const closeAddSkillModal = () => setIsAddSkillOpen(false);

  const upsertCompetence = (setFn, technology, difficulty_level, importance, extra = {}) => {
    setFn((prev) => {
      const idx = prev.findIndex((c) => c.technology === technology);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], difficulty_level, importance, ...extra };
        return next;
      }
      return [...prev, { technology, difficulty_level, importance, ...extra }];
    });
  };

  const handleSaveNewSkill = () => {
    const name = normalizeSkillName(newSkillName);
    const type = newSkillType;
    const difficulty = newSkillDifficulty || difficultyChoices[0];
    const importance = newSkillImportance || "primary";

    if (!name || !type || !difficulty) {
      toast({
        title: t("asapSkills.form.addSkill.missingFields", {
          defaultValue: "Please fill skill name, type, and difficulty.",
        }),
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Enforce max skills: adding implies selecting it.
    if (totalSelectedSkills >= MAX_SKILLS) {
      toast({
        title: t("asapSkills.alerts.maxSkillsReached"),
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const lower = name.toLowerCase();
    const includesName = (arr) => (arr || []).some((s) => getCustomSkillName(s).toLowerCase() === lower);

    if (type === "technique") {
      const technicalSubcategory = normalizeTechnicalSubcategory(
        newSkillTechnicalSubcategory
      );
      const requiresCoding = technicalSubcategory === "dev";
      const existsInMatrix = (technicalItems || []).some(
        (it) => String(it?.["compétence_technique"] || "").trim().toLowerCase() === lower
      );
      if (!existsInMatrix) {
        setCustomTechSkills((prev) => {
          const idx = (prev || []).findIndex(
            (it) => getCustomSkillName(it).toLowerCase() === lower
          );
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = {
              ...(typeof next[idx] === "object" && next[idx] ? next[idx] : { name }),
              name,
              technical_subcategory: technicalSubcategory,
              requiresCoding,
            };
            return next;
          }
          return [
            ...prev,
            {
              name,
              technical_subcategory: technicalSubcategory,
              requiresCoding,
            },
          ];
        });
      }
      upsertCompetence(setTechCompetences, name, difficulty, importance, {
        requiresCoding,
        technical_subcategory: technicalSubcategory,
      });
    } else if (type === "fonctionnel") {
      const existsInMatrix = (functionalItems || []).some(
        (it) => String(it?.["compétence_fonctionnelle"] || "").trim().toLowerCase() === lower
      );
      if (!existsInMatrix && !includesName(customFuncSkills)) {
        setCustomFuncSkills((prev) => [...prev, name]);
      }
      upsertCompetence(setFuncCompetences, name, difficulty, importance);
    } else if (type === "metier") {
      const existsInMatrix = (businessItems || []).some(
        (it) => String(it?.["compétence_metier"] || "").trim().toLowerCase() === lower
      );
      if (!existsInMatrix && !includesName(customBusinessSkills)) {
        setCustomBusinessSkills((prev) => [...prev, name]);
      }
      upsertCompetence(setMetierCompetences, name, difficulty, importance);
    } else if (type === "role") {
      // Role is treated as functional for submission, and displayed in Role section.
      if (!includesName(customRoleSkills)) {
        setCustomRoleSkills((prev) => [...prev, name]);
      }
      upsertCompetence(setFuncCompetences, name, difficulty, importance, {
        isRole: true,
      });
    }

    setIsAddSkillOpen(false);
  };

  // Load job matrix after selecting a job description file
  useEffect(() => {
    const fetchJobMatrixData = async () => {
      if (!jobDescFile) {
        setJobMatrixData(null);
        setSector("");
        setSubSector("");
        setTechCompetences([]);
        setFuncCompetences([]);
        setMetierCompetences([]);
        return;
      }
      setLoadingJobMatrix(true);
      try {
        const formData = new FormData();
        formData.append("jobDescFile", jobDescFile);
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_API_URL}/api/job-matrix`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (response.data) {
          setJobMatrixData(response.data);
          setSector(response.data?.secteur || "");
          setSubSector(response.data?.sous_secteur || "");
        }
      } catch (error) {
        console.error("Error fetching job matrix data:", error);
        toast({
          title: t("asapSkills.toasts.loadJobMatrixFailed"),
          status: "error",
        });
      } finally {
        setLoadingJobMatrix(false);
      }
    };
    fetchJobMatrixData();
  }, [jobDescFile, toast]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // At least one competence must be selected before submitting
  const hasSelectedCompetences = useMemo(
    () => techCompetences.length > 0 || funcCompetences.length > 0 || metierCompetences.length > 0,
    [techCompetences, funcCompetences, metierCompetences]
  );

  // Maximum skills limit
  const MAX_SKILLS = 5;
  const totalSelectedSkills = useMemo(
    () => techCompetences.length + funcCompetences.length + metierCompetences.length,
    [techCompetences, funcCompetences, metierCompetences]
  );
  const isMaxSkillsReached = totalSelectedSkills >= MAX_SKILLS;

  const allEmailsValid = useMemo(() => candidateRows.every((r) => r.email && validateEmail(r.email)), [candidateRows]);

  const handleAddRow = () => setCandidateRows((prev) => [...prev, { name: "", email: "", valid: false }]);
  const handleRemoveRow = (idx) => setCandidateRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const handleRowChange = (idx, field, value) => {
    setCandidateRows((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value, valid: field === "email" ? validateEmail(value) : row.valid } : row)));
  };

  const handleJobDescDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setJobDescDragActive(true);
    else if (e.type === "dragleave") setJobDescDragActive(false);
  };
  const handleJobDescDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setJobDescDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setJobDescFile(e.dataTransfer.files[0]);
    }
  };

  const buildEvaluationData = () => {
    const evaluationData = {
      candidate_emails: candidateRows.map((r) => r.email),
      candidate_names: candidateRows.map((r) => r.name),
      company_code: companyCode,
      language,
      auto_mode: autoMode,
    };
    if (linkedConsultantId) {
      evaluationData.consultant_id = linkedConsultantId;
    }
    if (sector) {
      evaluationData.secteur = sector;
      if (subSector) evaluationData.sous_secteur = subSector;
      if (jobMatrixData["synthèse_mission"]) evaluationData.job_desc_summary = jobMatrixData["synthèse_mission"];
    }
    if (!autoMode) {
      evaluationData.job_matrix = [];
      techCompetences.forEach(
        ({ technology, difficulty_level, importance, technical_subcategory, skill_context, isRole }) => {
        evaluationData.job_matrix.push({
          technology,
          difficulty_level,
          importance,
          type: "technique",
          ...(technical_subcategory ? { technical_subcategory } : {}),
          ...(typeof skill_context === "string" ? { skill_context } : {}),
          ...(isRole === true ? { isRole: true } : {}),
        });
      });
      funcCompetences.forEach(({ technology, difficulty_level, importance, skill_context, isRole }) => {
        evaluationData.job_matrix.push({
          technology,
          difficulty_level,
          importance,
          type: "fonctionnel",
          ...(typeof skill_context === "string" ? { skill_context } : {}),
          ...(isRole === true ? { isRole: true } : {}),
        });
      });
      metierCompetences.forEach(({ technology, difficulty_level, importance, skill_context, isRole }) => {
        evaluationData.job_matrix.push({
          technology,
          difficulty_level,
          importance,
          type: "metier",
          ...(typeof skill_context === "string" ? { skill_context } : {}),
          ...(isRole === true ? { isRole: true } : {}),
        });
      });
    }
    if (userHasSetDuration && durationMinutes != null && skillsCount > 0) {
      evaluationData.test_duration_minutes = durationMinutes;
    }
    return evaluationData;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!hasSelectedBU) {
      toast({
        title: noBUAvailable
          ? t("asapSkills.alerts.noBusinessUnitTitle")
          : t("asapSkills.alerts.businessUnitRequiredTitle"),
        description: noBUAvailable
          ? t("asapSkills.alerts.noBusinessUnitDescription")
          : t("asapSkills.alerts.businessUnitRequiredDescription"),
        status: "warning",
      });
      return;
    }
    if (!allEmailsValid) {
      toast({
        title: t("asapSkills.toasts.invalidEmails"),
        status: "error",
      });
      return;
    }
    if (!autoMode && !hasSelectedCompetences) {
      toast({
        title: t("asapSkills.toasts.noCompetences"),
        status: "error",
      });
      return;
    }
    if (autoMode && skillsCount === 0) {
      toast({
        title: t("asapSkills.toasts.noDetectedSkillsTitle"),
        description: t("asapSkills.toasts.noDetectedSkillsDescription"),
        status: "error",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      if (jobDescFile) formData.append("jobDescFile", jobDescFile);
      formData.append("evaluation_data", JSON.stringify(buildEvaluationData()));
      await axios.post(`${process.env.REACT_APP_BASE_API_URL}/api/prepare/evaluation`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast({
        title: t("asapSkills.toasts.invitationsSent"),
        status: "success",
      });
      // Refetch assessments shortly after to display new loading card
      setTimeout(() => {
        fetchDocuments(false);
      }, 1000);
      // Reset minimal fields
      setCandidateRows([{ name: "", email: "", valid: false }]);
      setJobDescFile(null);
      setTechCompetences([]);
      setFuncCompetences([]);
      setMetierCompetences([]);
      
    } catch (error) {
      console.error(error);
      toast({
        title: t("asapSkills.toasts.submitErrorTitle"),
        description: error.response?.data?.message,
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%" display="flex" flexDirection="column">
      <PageHeader
        moduleCode="SK"
        eyebrow={t("asapSkills.eyebrow", "ASAP Skills")}
        title={t("asapSkills.pageTitle")}
      />

      <Box p="20px" flex="1" minH="0" display="flex" flexDirection="column">
        {linkedConsultantId && (
          <Alert status="info" mb="12px" borderRadius="10px" variant="left-accent">
            <AlertIcon />
            <Text fontSize="12px" flex={1} sx={{ marginBottom: "0 !important" }}>
              {t("asapSkills.linkedConsultantBanner", {
                defaultValue: "This evaluation will be linked to {{name}}",
                name: linkedConsultantName || `Consultant #${linkedConsultantId}`,
              })}
            </Text>
            <CloseButton size="sm" onClick={unlinkConsultant} />
          </Alert>
        )}

        <Flex gap="12px" flex="1" minH="0" direction={{ base: "column", md: "row" }}>
          {/* Left — assessment documents list */}
          <Box flex={{ base: "1 1 100%", md: "0 0 40%" }} minW={{ md: "320px" }} minH="0" display="flex">
            <Panel
              w="100%"
              h="100%"
              title={t("asapSkills.documentsTitle")}
              count={totalItems != null ? totalItems : (isLoadingDocs ? undefined : groups.length)}
              filters={
                <FilterSearch
                  placeholder={t("asapSkills.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  data-testid="skills-doc-search"
                />
              }
              bodyProps={{ p: "12px" }}
              footer={
                <Flex
                  minH="39px"
                  borderTopWidth="1px"
                  borderColor="portal.line"
                  align="center"
                  justify="space-between"
                  px="12px"
                  bg="portal.panel"
                >
                  <Text fontSize="11px" fontWeight="700" color="#667a89" sx={{ marginBottom: "0 !important" }}>
                    {t("asapSkills.pagination.pageLabel", { page })}
                  </Text>
                  <HStack spacing="4px">
                    <Button
                      variant="secondary"
                      size="xs"
                      h="25px"
                      fontSize="11px"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      isDisabled={page <= 1}
                    >
                      {t("asapSkills.pagination.previous")}
                    </Button>
                    <Button
                      variant="secondary"
                      size="xs"
                      h="25px"
                      fontSize="11px"
                      onClick={() => setPage((p) => p + 1)}
                      isDisabled={!hasNextPage}
                    >
                      {t("asapSkills.pagination.next")}
                    </Button>
                  </HStack>
                </Flex>
              }
            >
              <Box data-testid="skills-doc-list-block">
                {isLoadingDocs ? (
                  <Center py="40px">
                    <Spinner color="portal.primary" />
                  </Center>
                ) : groups.length === 0 ? (
                  <EmptyState
                    icon={<FaClipboardList />}
                    title={t("asapSkills.noAssessments")}
                    hint={t("asapSkills.emptyHint", "Send an assessment from the form to see it here.")}
                  />
                ) : (
                  <VStack align="stretch" spacing="12px">
                    {groups.map((g) => {
              const fullName = `${g.candidate_first_name || ""} ${g.candidate_last_name || ""}`.trim() || g.req_id || g.id;
              const updated = g.updated_at ? new Date(g.updated_at).toLocaleString() : (g.created_at ? new Date(g.created_at).toLocaleString() : "");
              // Derive assessment "stage" from backend flags/timestamps.
              // Backend hints:
              // - Created: DocumentGroup exists but assessment_job_desc_url is null.
              // - Prepared / sent: assessment_job_desc_url is set (and usually assessment_generated_at + assessment_skills are set).
              // - In progress: assessment_started_at is set while assessment_completed_at is still null.
              // - Completed + scored: assessment_completed_at is set and assessment_score is set.
              // - Report generated: assessment_report_url is set.
              const jobDescUrl =
                g.assessment_job_desc_url ||
                g.assessment_job_description_url ||
                g.assessment_job_url ||
                null;
              const generatedAt = g.assessment_generated_at || g.generated_at || null;
              const startedAt = g.assessment_started_at || g.started_at || g.start_time || null;
              const completedAt = g.assessment_completed_at || g.completed_at || g.end_time || null;
              const reportUrl = g.assessment_report_url || null;
              const scoreVal = g.assessment_score;

              const isSent = Boolean(jobDescUrl || generatedAt || g.assessment_skills);
              const isStarted = Boolean(startedAt);
              const isCompleted = Boolean(completedAt);
              const isScored = scoreVal !== undefined && scoreVal !== null;
              const isReportReady = Boolean(reportUrl);

              // Keep the existing skeleton/progress card only for the "created / preparing" stage.
              const isLoadingCard = !isSent && !isStarted && !isCompleted && !isReportReady;

              // Display only the latest / most-advanced status.
              const primaryStatusBadge = (() => {
                if (isReportReady) {
                  return {
                    key: "reportReady",
                    label: t("asapSkills.card.status.reportReady", { defaultValue: "Report ready" }),
                    colorScheme: "purple",
                  };
                }
                if (isCompleted) {
                  return {
                    key: isScored ? "completed" : "completedPendingScore",
                    label: isScored
                      ? t("asapSkills.card.status.completed", { defaultValue: "Completed" })
                      : t("asapSkills.card.status.completedPendingScore", {
                          defaultValue: "Completed (scoring)",
                        }),
                    colorScheme: "green",
                  };
                }
                if (isStarted) {
                  return {
                    key: "started",
                    label: t("asapSkills.card.status.started", { defaultValue: "Started" }),
                    colorScheme: "blue",
                  };
                }
                if (isSent) {
                  return {
                    key: "sent",
                    label: t("asapSkills.card.status.sent", { defaultValue: "Sent" }),
                    colorScheme: "yellow",
                  };
                }
                return {
                  key: "preparing",
                  label: t("asapSkills.card.status.preparing", { defaultValue: "Preparing" }),
                  colorScheme: "gray",
                };
              })();
              
              // Parse skills using the same logic as AsapCV
              const parseSkillsLocal = (raw) => {
                if (!raw) return [];
                if (Array.isArray(raw)) return raw;
                if (typeof raw === "string") {
                  try {
                    const parsed = JSON.parse(raw);
                    if (Array.isArray(parsed)) return parsed;
                  } catch (_) {
                    // not JSON, fall through
                  }
                  return String(raw).split(",").map((s) => s.trim()).filter(Boolean);
                }
                return [];
              };
              let techSkills = parseSkillsLocal(g.skills_technique);
              let projectSkills = parseSkillsLocal(g.skills_projet);
              let businessSkills = parseSkillsLocal(g.skills_metier);
              // Backward compatibility with legacy tested_skills and key_skills
              if (techSkills.length === 0 && projectSkills.length === 0 && businessSkills.length === 0) {
                const testedSkills = parseSkillsLocal(g.tested_skills || g.key_skills);
                techSkills = testedSkills; // Default to technical skills for backward compatibility
              }
              // Dynamically pick downloadable urls; derive kind by removing trailing _url
              const urlKeys = Object.keys(g || {}).filter((k) => k.endsWith("_url") && g[k]);
              return (
                <Box
                  key={g.id || g.req_id}
                  data-testid={`skills-doc-card-${g.id || g.req_id}`}
                  borderWidth="1px"
                  borderColor="portal.line"
                  bg="portal.panel"
                  borderRadius="10px"
                  p="14px"
                  transition="box-shadow .15s, background .15s"
                  _hover={{ boxShadow: "kpi", bg: "portal.soft" }}
                >
                  <HStack align="start" spacing="12px">
                    <Avatar name={fullName} size="sm" bg="portal.active" color="portal.primary" />
                    <Box flex="1" minW="0">
                      <HStack justify="space-between" align="start" spacing={2}>
                        <Box minW="0">
                          <Skeleton isLoaded={!isLoadingCard} fadeDuration={0.2} w="fit-content">
                            <Text
                              fontSize="13px"
                              fontWeight="700"
                              color="portal.text"
                              noOfLines={1}
                              sx={{ marginBottom: "2px !important" }}
                              data-testid={`skills-doc-name-${g.id || g.req_id}`}
                            >
                              {fullName}
                            </Text>
                          </Skeleton>
                        </Box>
                        <VStack spacing="6px" align="end" flexShrink={0}>
                          {!isLoadingCard && isCompleted && g.assessment_score != null && (
                            <Badge
                              colorScheme="purple"
                              variant="solid"
                              borderRadius="999px"
                              px="8px"
                              fontSize="11px"
                              data-testid={`skills-doc-score-${g.id || g.req_id}`}
                            >
                              {t("asapSkills.card.scoreTag", {
                                score: g.assessment_score,
                              })}
                            </Badge>
                          )}
                          <StatusBadge
                            key={primaryStatusBadge.key}
                            label={primaryStatusBadge.label}
                            colorScheme={primaryStatusBadge.colorScheme}
                          />
                        </VStack>
                      </HStack>

                      {(techSkills.length > 0 || projectSkills.length > 0 || businessSkills.length > 0) && (
                        <VStack align="start" spacing="6px" mt="10px">
                          {isLoadingCard ? (
                            <>
                              <Skeleton isLoaded={false} h="16px" w="80%" borderRadius="md" />
                              <Skeleton isLoaded={false} h="16px" w="60%" borderRadius="md" />
                            </>
                          ) : (
                            <>
                              {techSkills.length > 0 && (
                                <Wrap spacing="6px">
                                  {techSkills.slice(0, 6).map((s, idx) => (
                                    <WrapItem key={`tech-${idx}`}>
                                      <Badge colorScheme="purple" variant="subtle">{s}</Badge>
                                    </WrapItem>
                                  ))}
                                </Wrap>
                              )}
                              {projectSkills.length > 0 && (
                                <Wrap spacing="6px">
                                  {projectSkills.slice(0, 6).map((s, idx) => (
                                    <WrapItem key={`proj-${idx}`}>
                                      <Badge colorScheme="orange" variant="subtle">{s}</Badge>
                                    </WrapItem>
                                  ))}
                                </Wrap>
                              )}
                              {businessSkills.length > 0 && (
                                <Wrap spacing="6px">
                                  {businessSkills.slice(0, 6).map((s, idx) => (
                                    <WrapItem key={`metier-${idx}`}>
                                      <Badge colorScheme="green" variant="subtle">{s}</Badge>
                                    </WrapItem>
                                  ))}
                                </Wrap>
                              )}
                            </>
                          )}
                        </VStack>
                      )}

                      <Divider my="10px" borderColor="portal.line" />
                      <HStack spacing="8px" justify="space-between" align="center">
                        <Text fontSize="11px" color="portal.muted" noOfLines={1} sx={{ marginBottom: "0 !important" }}>
                          {t("asapSkills.card.updatedAt", { date: updated })}
                        </Text>
                        <HStack spacing="6px" flexShrink={0}>
                          {urlKeys.length === 0 ? (
                            isLoadingCard ? (
                              <Skeleton
                                isLoaded={false}
                                h="16px"
                                w="120px"
                                borderRadius="md"
                              />
                            ) : (
                              <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                {t("asapSkills.card.noFiles")}
                              </Text>
                            )
                          ) : (
                            urlKeys.map((key) => {
                              const rawKind = key.replace(/_url$/, "");
                              const kind =
                                rawKind === "assessment_job_desc" ||
                                rawKind === "assessment_job_description"
                                  ? "assessment_job"
                                  : rawKind;
                              const labelMap = {
                                assessment_job: t(
                                  "asapSkills.card.downloads.assessmentJob"
                                ),
                                assessment_report: t(
                                  "asapSkills.card.downloads.assessmentReport"
                                ),
                              };
                              const iconMap = {
                                assessment_job: FaClipboard,
                                assessment_report: FaFileAlt,
                              };
                              const label =
                                labelMap[kind] ||
                                rawKind.replace(/_/g, " ");
                              const IconComp = iconMap[kind] || FaFileAlt;
                              return (
                                <Tooltip
                                  key={key}
                                  label={label}
                                  placement="top"
                                  hasArrow
                                >
                                  <MiniIconButton
                                    aria-label={label}
                                    icon={<Icon as={IconComp} />}
                                    color="portal.primary"
                                    isDisabled={isLoadingCard}
                                    onClick={() =>
                                      handleKindDownload(
                                        g.req_id,
                                        kind,
                                        fullName
                                      )
                                    }
                                    isLoading={
                                      downloadingKey ===
                                      `${g.req_id}:${kind}`
                                    }
                                  />
                                </Tooltip>
                              );
                            })
                          )}
                        </HStack>
                      </HStack>
                      {isLoadingCard && (
                        <Progress mt="10px" size="xs" colorScheme="green" isIndeterminate borderRadius="999px" />
                      )}
                    </Box>
                  </HStack>
                </Box>
              );
            })}
                  </VStack>
                )}
              </Box>
            </Panel>
          </Box>

          {/* Right — assessment submission form */}
          <Box flex="1" minH="0" display="flex">
            <Panel
              w="100%"
              h="100%"
              title={t("asapSkills.form.panelTitle", "New assessment")}
              bodyProps={{ p: "16px" }}
            >
              {/* Monthly activity chart */}
              <Box mb="16px">
                <Heading
                  as="h3"
                  fontSize="11px"
                  fontWeight="800"
                  textTransform="uppercase"
                  letterSpacing="0.055em"
                  color="portal.muted"
                  sx={{ marginBottom: "8px !important" }}
                >
                  {t("asapSkills.chart.title")}
                </Heading>
                <Stack direction="row" align="flex-end" spacing="10px" h="78px" pt="6px" overflow="hidden">
                  {(() => {
                    const maxValue = Math.max(...monthData.map((m) => m.value), 1);
                    const maxHeight = 60;
                    return monthData.map((m) => {
                      const barHeight = m.value === 0 ? 2 : Math.max(8, (m.value / maxValue) * maxHeight);
                      return (
                        <Box key={m.label} textAlign="center" flex="1">
                          <Tooltip
                            label={t("asapSkills.chart.tooltip", { count: m.value })}
                            placement="top"
                            hasArrow
                          >
                            <Box
                              bgGradient="linear(180deg, #1E4D78 0%, #153B69 100%)"
                              w="100%"
                              borderRadius="6px"
                              h={`${barHeight}px`}
                              transition="height 0.3s"
                            />
                          </Tooltip>
                          <Text fontSize="10px" color="portal.muted" mt="4px" sx={{ marginBottom: "0 !important" }}>
                            {m.label}
                          </Text>
                        </Box>
                      );
                    });
                  })()}
                </Stack>
              </Box>

              <Divider mb="16px" borderColor="portal.line" />

              <form onSubmit={handleSubmit}>
          <Stack spacing="14px">
            {isBUContextLoaded && !hasSelectedBU && (
              <Alert status="warning" borderRadius="10px">
                <AlertIcon />
                <Text fontSize="12px" sx={{ marginBottom: "0 !important" }}>
                  {noBUAvailable
                    ? t("asapSkills.alerts.noBusinessUnitDescription")
                    : t("asapSkills.alerts.businessUnitRequiredDescription")}
                </Text>
              </Alert>
            )}
            <FormControl>
              <FormLabel {...formLabelProps}>{t("asapSkills.form.languageLabel")}</FormLabel>
              <Select
                {...selectProps}
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="francais">
                  {t("asapSkills.form.languageOptions.fr")}
                </option>
                <option value="anglais">
                  {t("asapSkills.form.languageOptions.en")}
                </option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel {...formLabelProps}>{t("asapSkills.form.uploadJobDescLabel", "Job description")}</FormLabel>
              {jobDescFile ? (
                <Tag size="md" variant="subtle" colorScheme="blue" borderRadius="999px">
                  <TagLabel>{jobDescFile.name}</TagLabel>
                  <TagCloseButton onClick={() => setJobDescFile(null)} />
                </Tag>
              ) : (
                <Box
                  border="1px dashed"
                  borderColor={jobDescDragActive ? "portal.primary" : "portal.line"}
                  borderRadius="10px"
                  p="16px"
                  textAlign="center"
                  minH="120px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg={jobDescDragActive ? "portal.active" : "portal.soft"}
                  transition="all 0.15s"
                  cursor="pointer"
                  onClick={() => jobDescInputRef.current?.click()}
                  onDragEnter={handleJobDescDrag}
                  onDragLeave={handleJobDescDrag}
                  onDragOver={handleJobDescDrag}
                  onDrop={handleJobDescDrop}
                  _hover={{ bg: "portal.active", borderColor: "portal.primary" }}
                >
                  <VStack spacing="6px">
                    <Icon as={FaCloudUploadAlt} boxSize="22px" color="portal.muted" />
                    <Text fontSize="12px" fontWeight="600" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                      {t("asapSkills.form.uploadJobDescDropText")}
                    </Text>
                    <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                      {t("asapSkills.form.acceptedFormats")}
                    </Text>
                  </VStack>
                  <Input type="file" accept=".pdf,.docx" ref={jobDescInputRef} onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setJobDescFile(file);
                  }} display="none" />
                </Box>
              )}
            </FormControl>

            {/* Job matrix selections - always visible */}
            <Box>
              {jobMatrixData && (
                <Box
                  mb="14px"
                  borderWidth="1px"
                  borderColor="portal.line"
                  borderRadius="10px"
                  p="12px"
                  bg="portal.soft"
                >
                  <Heading
                    as="h3"
                    fontSize="11px"
                    fontWeight="800"
                    textTransform="uppercase"
                    letterSpacing="0.055em"
                    color="portal.muted"
                    sx={{ marginBottom: "10px !important" }}
                  >
                    {t("asapSkills.form.sector.title", { defaultValue: "Sector information" })}
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                    <FormControl>
                      <FormLabel {...formLabelProps}>
                        {t("asapSkills.form.sector.sectorLabel", { defaultValue: "Sector" })}
                      </FormLabel>
                      <Input
                        size="sm"
                        h="31px"
                        value={sector}
                        onChange={(e) => setSector(e.target.value)}
                        placeholder={t("asapSkills.form.sector.sectorPlaceholder", {
                          defaultValue: "e.g. PropTech",
                        })}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel {...formLabelProps}>
                        {t("asapSkills.form.sector.subSectorLabel", { defaultValue: "Sub-sector" })}
                      </FormLabel>
                      <Input
                        size="sm"
                        h="31px"
                        value={subSector}
                        onChange={(e) => setSubSector(e.target.value)}
                        placeholder={t("asapSkills.form.sector.subSectorPlaceholder", {
                          defaultValue: "e.g. Hospitality / Flexible Living SaaS",
                        })}
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>
              )}
              <Heading
                as="h3"
                fontSize="13px"
                fontWeight="700"
                letterSpacing="-0.02em"
                color="portal.text"
                sx={{ marginBottom: "10px !important" }}
              >
                {t("asapSkills.form.evaluationSettingsTitle")}
              </Heading>
              {!autoMode && jobMatrixData && isMaxSkillsReached && (
                <Alert
                  status="warning"
                  borderRadius="10px"
                  mb="12px"
                  alignItems="center"
                >
                  <AlertIcon />
                  <Text fontSize="12px" sx={{ marginBottom: "0 !important" }}>
                    {t("asapSkills.alerts.maxSkillsReached")}
                  </Text>
                </Alert>
              )}
              {loadingJobMatrix ? (
                <Center py="20px"><Spinner color="portal.primary" /></Center>
              ) : jobMatrixData ? (
                <Stack spacing={4}>
                  {/* Role first, then technical/functional/business */}
                  {roleDisplayItems.length > 0 && (
                    <Box
                      borderWidth="1px"
                      borderRadius="10px"
                      borderColor={MATRIX_SECTIONS.role.border}
                      bg={MATRIX_SECTIONS.role.bg}
                      p="12px"
                    >
                      <Heading
                        as="h4"
                        fontSize="11px"
                        fontWeight="800"
                        textTransform="uppercase"
                        letterSpacing="0.055em"
                        color={MATRIX_SECTIONS.role.heading}
                        sx={{ marginBottom: "8px !important" }}
                      >
                        {t("asapSkills.form.sections.role")}
                      </Heading>
                      <VStack align="stretch" spacing="6px">
                        {roleDisplayItems.map((item, index) => {
                          const { technology, sourceType } = item;
                          const selectedArray =
                            sourceType === "technique"
                              ? techCompetences
                              : sourceType === "fonctionnel"
                              ? funcCompetences
                              : metierCompetences;
                          const selected = selectedArray.find(
                            (c) => c.technology === technology
                          );
                          const levelOptions = difficultyChoices;
                          const defaultImportance =
                            item?.importance === "secondary" ? "secondary" : "primary";
                          const requiresCoding =
                            sourceType === "technique" && isCodingSkill(item);

                          const updateArray = (updater) => {
                            if (sourceType === "technique") {
                              setTechCompetences((prev) => updater(prev));
                            } else if (sourceType === "fonctionnel") {
                              setFuncCompetences((prev) => updater(prev));
                            } else {
                              setMetierCompetences((prev) => updater(prev));
                            }
                          };

                          return (
                            <HStack
                              key={`${technology}-${sourceType}-${index}${item.__custom ? "-custom" : ""}`}
                              align="center"
                              spacing={3}
                            >
                              <Checkbox
                                size="sm"
                                flex="1"
                                isChecked={!!selected}
                                isDisabled={autoMode || (isMaxSkillsReached && !selected)}
                                onChange={(e) =>
                                  updateArray((prev) =>
                                    e.target.checked
                                      ? [
                                          ...prev,
                                          {
                                            technology,
                                            difficulty_level: difficultyChoices[0],
                                            importance: defaultImportance,
                                            requiresCoding,
                                            isRole: true,
                                            technical_subcategory: item?.technical_subcategory,
                                            skill_context: item?.skill_context,
                                          },
                                        ]
                                      : prev.filter((c) => c.technology !== technology)
                                  )
                                }
                              >
                                {renderSkillLabelWithContext(
                                  technology,
                                  item?.skill_context,
                                  requiresCoding
                                )}
                              </Checkbox>
                              <Select
                                size="xs"
                                maxW="160px"
                                value={selected?.difficulty_level || difficultyChoices[0]}
                                isDisabled={!selected || autoMode}
                                onChange={(e) =>
                                  updateArray((prev) =>
                                    prev.map((c) =>
                                      c.technology === technology
                                        ? { ...c, difficulty_level: e.target.value }
                                        : c
                                    )
                                  )
                                }
                                {...matrixSelectProps}
                              >
                                {levelOptions.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </Select>
                              <Select
                                size="xs"
                                maxW="140px"
                                value={selected?.importance || defaultImportance}
                                isDisabled={!selected || autoMode}
                                onChange={(e) =>
                                  updateArray((prev) =>
                                    prev.map((c) =>
                                      c.technology === technology
                                        ? { ...c, importance: e.target.value }
                                        : c
                                    )
                                  )
                                }
                                {...matrixSelectProps}
                              >
                                {importanceOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Select>
                              <ImportanceHelpIcon />
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  {technicalDisplayItems.length > 0 && (
                    <Box
                      borderWidth="1px"
                      borderRadius="10px"
                      borderColor={MATRIX_SECTIONS.technical.border}
                      bg={MATRIX_SECTIONS.technical.bg}
                      p="12px"
                    >
                      <Heading
                        as="h4"
                        fontSize="11px"
                        fontWeight="800"
                        textTransform="uppercase"
                        letterSpacing="0.055em"
                        color={MATRIX_SECTIONS.technical.heading}
                        sx={{ marginBottom: "8px !important" }}
                      >
                        {t("asapSkills.form.sections.technical")}
                      </Heading>
                      <VStack align="stretch" spacing="6px">
                        {technicalDisplayItems.map((item, index) => {
                          const technology = item["compétence_technique"];
                          const selected = techCompetences.find(
                            (c) => c.technology === technology
                          );
                          const levelOptions = difficultyChoices;
                          const defaultImportance =
                            item?.importance === "secondary" ? "secondary" : "primary";
                          const requiresCoding = isCodingSkill(item);
                          return (
                            <HStack
                              key={`${technology || index}${item.__custom ? "-custom" : ""}`}
                              align="center"
                              spacing={3}
                            >
                              <Checkbox
                                size="sm"
                                flex="1"
                                isChecked={!!selected}
                                isDisabled={autoMode || (isMaxSkillsReached && !selected)}
                                onChange={(e) =>
                                  setTechCompetences((prev) =>
                                    e.target.checked
                                      ? [
                                          ...prev,
                                          {
                                            technology,
                                            difficulty_level: difficultyChoices[0],
                                            importance: defaultImportance,
                                            requiresCoding,
                                            technical_subcategory: item?.technical_subcategory,
                                            skill_context: item?.skill_context,
                                          },
                                        ]
                                      : prev.filter((c) => c.technology !== technology)
                                  )
                                }
                              >
                                {renderSkillLabelWithContext(
                                  technology,
                                  item?.skill_context,
                                  requiresCoding
                                )}
                              </Checkbox>
                              <Select
                                size="xs"
                                maxW="160px"
                                value={selected?.difficulty_level || difficultyChoices[0]}
                                isDisabled={!selected || autoMode}
                                onChange={(e) =>
                                  setTechCompetences((prev) =>
                                    prev.map((c) =>
                                      c.technology === technology
                                        ? { ...c, difficulty_level: e.target.value }
                                        : c
                                    )
                                  )
                                }
                                {...matrixSelectProps}
                              >
                                {levelOptions.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </Select>
                              <Select
                                size="xs"
                                maxW="140px"
                                value={selected?.importance || defaultImportance}
                                isDisabled={!selected || autoMode}
                                onChange={(e) =>
                                  setTechCompetences((prev) =>
                                    prev.map((c) =>
                                      c.technology === technology
                                        ? { ...c, importance: e.target.value }
                                        : c
                                    )
                                  )
                                }
                                {...matrixSelectProps}
                              >
                                {importanceOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Select>
                              <ImportanceHelpIcon />
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  {functionalDisplayItems.length > 0 && (
                    <Box
                      borderWidth="1px"
                      borderRadius="10px"
                      borderColor={MATRIX_SECTIONS.functional.border}
                      bg={MATRIX_SECTIONS.functional.bg}
                      p="12px"
                    >
                      <Heading
                        as="h4"
                        fontSize="11px"
                        fontWeight="800"
                        textTransform="uppercase"
                        letterSpacing="0.055em"
                        color={MATRIX_SECTIONS.functional.heading}
                        sx={{ marginBottom: "8px !important" }}
                      >
                        {t("asapSkills.form.sections.functional")}
                      </Heading>
                      <VStack align="stretch" spacing="6px">
                        {functionalDisplayItems.map((item, index) => {
                          const technology = item["compétence_fonctionnelle"];
                          const selected = funcCompetences.find(
                            (c) => c.technology === technology
                          );
                          const levelOptions = difficultyChoices;
                          const defaultImportance =
                            item?.importance === "secondary" ? "secondary" : "primary";
                          return (
                            <HStack
                              key={`${technology || index}${item.__custom ? "-custom" : ""}`}
                              align="center"
                              spacing={3}
                            >
                              <Checkbox
                                size="sm"
                                flex="1"
                                isChecked={!!selected}
                                isDisabled={
                                  autoMode ||
                                  (isMaxSkillsReached && !selected)
                                }
                                onChange={(e) =>
                                  setFuncCompetences((prev) =>
                                    e.target.checked
                                      ? [
                                          ...prev,
                                          {
                                            technology,
                                            difficulty_level: difficultyChoices[0],
                                            importance: defaultImportance,
                                            skill_context: item?.skill_context,
                                          },
                                        ]
                                      : prev.filter(
                                          (c) =>
                                            c.technology !== technology
                                        )
                                  )
                                }
                              >
                                {renderSkillLabelWithContext(
                                  technology,
                                  item?.skill_context
                                )}
                              </Checkbox>
                              <Select
                                size="xs"
                                maxW="160px"
                                value={
                                  selected?.difficulty_level || difficultyChoices[0]
                                }
                                isDisabled={!selected || autoMode}
                                onChange={(e) =>
                                  setFuncCompetences((prev) =>
                                    prev.map((c) =>
                                      c.technology === technology
                                        ? {
                                            ...c,
                                            difficulty_level: e.target.value,
                                          }
                                        : c
                                    )
                                  )
                                }
                                {...matrixSelectProps}
                              >
                                {levelOptions.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </Select>
                              <Select
                                size="xs"
                                maxW="140px"
                                value={selected?.importance || defaultImportance}
                                isDisabled={!selected || autoMode}
                                onChange={(e) =>
                                  setFuncCompetences((prev) =>
                                    prev.map((c) =>
                                      c.technology === technology
                                        ? { ...c, importance: e.target.value }
                                        : c
                                    )
                                  )
                                }
                                {...matrixSelectProps}
                              >
                                {importanceOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Select>
                              <ImportanceHelpIcon />
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  {businessDisplayItems.length > 0 && (
                    <Box
                      borderWidth="1px"
                      borderRadius="10px"
                      borderColor={MATRIX_SECTIONS.business.border}
                      bg={MATRIX_SECTIONS.business.bg}
                      p="12px"
                    >
                      <Heading
                        as="h4"
                        fontSize="11px"
                        fontWeight="800"
                        textTransform="uppercase"
                        letterSpacing="0.055em"
                        color={MATRIX_SECTIONS.business.heading}
                        sx={{ marginBottom: "8px !important" }}
                      >
                        {t("asapSkills.form.sections.business")}
                      </Heading>
                      <VStack align="stretch" spacing="6px">
                        {businessDisplayItems.map((item, index) => {
                          const technology = item["compétence_metier"];
                          const selected = metierCompetences.find(
                            (c) => c.technology === technology
                          );
                          const levelOptions = difficultyChoices;
                          const defaultImportance =
                            item?.importance === "secondary" ? "secondary" : "primary";
                          return (
                            <HStack
                              key={`${technology || index}${item.__custom ? "-custom" : ""}`}
                              align="center"
                              spacing={3}
                            >
                              <Checkbox
                                size="sm"
                                flex="1"
                                isChecked={!!selected}
                                isDisabled={
                                  autoMode ||
                                  (isMaxSkillsReached && !selected)
                                }
                                onChange={(e) =>
                                  setMetierCompetences((prev) =>
                                    e.target.checked
                                      ? [
                                          ...prev,
                                          {
                                            technology,
                                            difficulty_level: difficultyChoices[0],
                                            importance: defaultImportance,
                                            skill_context: item?.skill_context,
                                          },
                                        ]
                                      : prev.filter(
                                          (c) =>
                                            c.technology !== technology
                                        )
                                  )
                                }
                              >
                                {renderSkillLabelWithContext(
                                  technology,
                                  item?.skill_context
                                )}
                              </Checkbox>
                              <Select
                                size="xs"
                                maxW="160px"
                                value={
                                  selected?.difficulty_level || difficultyChoices[0]
                                }
                                isDisabled={!selected || autoMode}
                                onChange={(e) =>
                                  setMetierCompetences((prev) =>
                                    prev.map((c) =>
                                      c.technology === technology
                                        ? {
                                            ...c,
                                            difficulty_level: e.target.value,
                                          }
                                        : c
                                    )
                                  )
                                }
                                {...matrixSelectProps}
                              >
                                {levelOptions.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </Select>
                              <Select
                                size="xs"
                                maxW="140px"
                                value={selected?.importance || defaultImportance}
                                isDisabled={!selected || autoMode}
                                onChange={(e) =>
                                  setMetierCompetences((prev) =>
                                    prev.map((c) =>
                                      c.technology === technology
                                        ? { ...c, importance: e.target.value }
                                        : c
                                    )
                                  )
                                }
                                {...matrixSelectProps}
                              >
                                {importanceOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Select>
                              <ImportanceHelpIcon />
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  <Button
                    variant="secondary"
                    size="sm"
                    h="32px"
                    px="14px"
                    fontSize="12px"
                    leftIcon={<FaPlus />}
                    onClick={openAddSkillModal}
                    w="fit-content"
                  >
                    {t("asapSkills.form.addSkill.button", { defaultValue: "Add a skill" })}
                  </Button>

                </Stack>
              ) : (
                <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                  {t("asapSkills.form.emptyMatrixHint")}
                </Text>
              )}
              <Box mt="14px">
                <FormLabel {...formLabelProps}>
                  {t("asapSkills.form.durationLabel")}
                </FormLabel>
                {skillsCount > 0 ? (
                  <Box borderWidth="1px" borderColor="portal.line" borderRadius="10px" p="12px" maxW="420px" bg="portal.soft">
                    <Slider
                      min={sliderMin}
                      max={sliderMax}
                      step={0.25}
                      value={durationMinutes ?? sliderMax}
                      onChange={(v) => {
                        setUserHasSetDuration(true);
                        setDurationMinutes(v);
                      }}
                    >
                      <SliderTrack>
                        <SliderFilledTrack bg="portal.primary" />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                    <HStack justify="space-between" mt="4px">
                      <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {t("asapSkills.form.durationMin", {
                          value: sliderMin,
                        })}
                      </Text>
                      <Text fontSize="11px" fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                        {t("asapSkills.form.durationSelected", {
                          value: durationMinutes ?? sliderMax,
                        })}
                      </Text>
                      <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {t("asapSkills.form.durationMax", {
                          value: sliderMax,
                        })}
                      </Text>
                    </HStack>
                  </Box>
                ) : (
                  <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("asapSkills.form.durationHint")}
                  </Text>
                )}
              </Box>
            </Box>

            {/* Candidate emails */}
            <Box>
              <FormLabel {...formLabelProps}>
                {t("asapSkills.form.emailsSectionLabel")}
              </FormLabel>
              <VStack align="stretch" spacing="8px">
                {candidateRows.map((row, idx) => (
                  <HStack key={idx} align="center" spacing="8px">
                    <Input
                      size="sm"
                      h="31px"
                      placeholder={t(
                        "asapSkills.form.candidateNamePlaceholder"
                      )}
                      value={row.name}
                      onChange={(e) =>
                        handleRowChange(idx, "name", e.target.value)
                      }
                    />
                    <Input
                      size="sm"
                      h="31px"
                      placeholder={t(
                        "asapSkills.form.candidateEmailPlaceholder"
                      )}
                      value={row.email}
                      onChange={(e) =>
                        handleRowChange(idx, "email", e.target.value)
                      }
                      isInvalid={!!row.email && !row.valid}
                    />
                    <MiniIconButton
                      aria-label={t("asapSkills.form.removeCandidateAria")}
                      icon={<FaTrashAlt />}
                      color="portal.danger"
                      onClick={() => handleRemoveRow(idx)}
                    />
                  </HStack>
                ))}
                <Button
                  variant="secondary"
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="12px"
                  leftIcon={<FaPlus />}
                  onClick={handleAddRow}
                  w="fit-content"
                >
                  {t("asapSkills.form.addCandidateButton")}
                </Button>
              </VStack>
            </Box>

            <Button
              {...primaryBtnProps}
              leftIcon={<Icon as={FaPaperPlane} />}
              type="submit"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText={t("asapSkills.form.submitLoading", {
                defaultValue: "Sending…",
              })}
              w="fit-content"
              data-testid="skills-submit-btn"
              isDisabled={
                !hasSelectedBU ||
                !allEmailsValid ||
                (!autoMode && !hasSelectedCompetences) ||
                (autoMode && skillsCount === 0)
              }
            >
              {t("asapSkills.form.submitLabel", {
                defaultValue: "Send invitations",
              })}
            </Button>
          </Stack>
              </form>
            </Panel>
          </Box>
        </Flex>
      </Box>

      {/* Add skill modal */}
      <Modal isOpen={isAddSkillOpen} onClose={closeAddSkillModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t("asapSkills.form.addSkill.modalTitle", {
              defaultValue: "Add a skill",
            })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              <FormControl isRequired>
                <FormLabel {...formLabelProps}>
                  {t("asapSkills.form.addSkill.nameLabel", {
                    defaultValue: "Skill name",
                  })}
                </FormLabel>
                <Input
                  value={newSkillName}
                  onChange={(e) => setNewSkillName(e.target.value)}
                  placeholder={t("asapSkills.form.addSkill.namePlaceholder", {
                    defaultValue: "e.g. React, Scrum, Banking…",
                  })}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel {...formLabelProps}>
                  {t("asapSkills.form.addSkill.typeLabel", {
                    defaultValue: "Type",
                  })}
                </FormLabel>
                <Select
                  {...selectProps}
                  value={newSkillType}
                  onChange={(e) => setNewSkillType(e.target.value)}
                >
                  <option value="technique">
                    {t("asapSkills.form.addSkill.typeOptions.technical", {
                      defaultValue: "Technical",
                    })}
                  </option>
                  <option value="fonctionnel">
                    {t("asapSkills.form.addSkill.typeOptions.functional", {
                      defaultValue: "Functional",
                    })}
                  </option>
                  <option value="metier">
                    {t("asapSkills.form.addSkill.typeOptions.business", {
                      defaultValue: "Business / Domain",
                    })}
                  </option>
                  <option value="role">
                    {t("asapSkills.form.addSkill.typeOptions.role", {
                      defaultValue: "Role",
                    })}
                  </option>
                </Select>
              </FormControl>

              {newSkillType === "technique" && (
                <FormControl isRequired>
                  <FormLabel {...formLabelProps}>
                    {t("asapSkills.form.addSkill.technicalSubcategoryLabel", {
                      defaultValue: "Technical subcategory",
                    })}
                  </FormLabel>
                  <Select
                    {...selectProps}
                    value={newSkillTechnicalSubcategory}
                    onChange={(e) => setNewSkillTechnicalSubcategory(e.target.value)}
                  >
                    <option value="dev">
                      {t("asapSkills.form.addSkill.technicalSubcategoryOptions.dev", {
                        defaultValue: "Dev (coding)",
                      })}
                    </option>
                    <option value="non-dev">
                      {t("asapSkills.form.addSkill.technicalSubcategoryOptions.nonDev", {
                        defaultValue: "Non-dev (no coding)",
                      })}
                    </option>
                  </Select>
                </FormControl>
              )}

              <FormControl isRequired>
                <FormLabel {...formLabelProps}>
                  {t("asapSkills.form.importance.label", {
                    defaultValue: "Importance",
                  })}
                </FormLabel>
                <HStack spacing={1} align="center">
                  <Select
                    {...selectProps}
                    value={newSkillImportance}
                    onChange={(e) => setNewSkillImportance(e.target.value)}
                  >
                    {importanceOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                  <ImportanceHelpIcon />
                </HStack>
              </FormControl>

              <FormControl isRequired>
                <FormLabel {...formLabelProps}>
                  {t("asapSkills.form.addSkill.difficultyLabel", {
                    defaultValue: "Difficulty",
                  })}
                </FormLabel>
                <Select
                  {...selectProps}
                  value={newSkillDifficulty}
                  onChange={(e) => setNewSkillDifficulty(e.target.value)}
                >
                  {difficultyChoices.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" size="sm" h="32px" px="14px" fontSize="12px" mr={3} onClick={closeAddSkillModal}>
              {t("asapSkills.form.addSkill.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button {...primaryBtnProps} onClick={handleSaveNewSkill}>
              {t("asapSkills.form.addSkill.save", { defaultValue: "Save" })}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AsapSkills;

